// recaptcha.js — lazy-loaded Google reCAPTCHA v3, ported verbatim from
// HypeTunes_Web_2026/src/utils/recaptcha.js so the salon signup (GuestClaim)
// passes the same bot check the website's register form does. The API's
// /api/auth/register verifies with expectedAction 'register' whenever
// RECAPTCHA_SECRET_KEY is set on the server — which it is on Khari's local
// API too, so this is needed in dev, not just prod.
//
// Site key: VITE_RECAPTCHA_SITE_KEY (same public key as the web app —
// localhost and www.hypetunes.com are both whitelisted in the console).
// If unset, getRecaptchaToken resolves to '' and the server's soft-fail
// path decides.

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

let loaderPromise = null;

function loadScript() {
  if (!SITE_KEY) return Promise.resolve(false);
  if (loaderPromise) return loaderPromise;

  loaderPromise = new Promise((resolve) => {
    if (typeof window === 'undefined') return resolve(false);
    if (window.grecaptcha && window.grecaptcha.execute) return resolve(true);

    const id = 'recaptcha-v3-script';
    if (document.getElementById(id)) {
      const tick = () => {
        if (window.grecaptcha && window.grecaptcha.execute) resolve(true);
        else setTimeout(tick, 100);
      };
      return tick();
    }

    const s = document.createElement('script');
    s.id = id;
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(SITE_KEY)}`;
    s.async = true;
    s.defer = true;
    s.onload = () => {
      window.grecaptcha.ready(() => resolve(true));
    };
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });

  return loaderPromise;
}

/**
 * Returns a one-shot reCAPTCHA v3 token, or '' if the site key is not
 * configured (in which case the API will skip verification anyway).
 */
export async function getRecaptchaToken(action = 'submit') {
  const ok = await loadScript();
  if (!ok || !window.grecaptcha) return '';
  try {
    return await window.grecaptcha.execute(SITE_KEY, { action });
  } catch (err) {
    console.warn('[recaptcha] execute failed:', err);
    return '';
  }
}
